import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  getProjectWorkTypesBySection,
  updateProjectProgressWorkType,
} from "../../../../api/projectProgressApi";
import {
  calcDurationDays,
  clampPercent,
  formatDate,
  formatDaysWord,
  formatPercent,
  resolveWorkTypeRelationIds,
  splitSectionName,
  toDateInputValue,
} from "../projectsScheduleHelpers";
import WorkTypeRelationPicker from "./WorkTypeRelationPicker";
import "./EditWorkTypeDrawer.css";

const emptyForm = {
  name: "",
  unit_weight_percent: 0,
  plan_start_date: "",
  plan_end_date: "",
  fact_start_date: "",
  fact_end_date: "",
  completion_percent: 0,
  order_index: 0,
};

const LinkIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden>
    <path
      d="M10 13a5 5 0 0 0 7.07 0l2.12-2.12a5 5 0 0 0-7.07-7.07L10.7 5.23"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
    />
    <path
      d="M14 11a5 5 0 0 0-7.07 0L4.81 13.12a5 5 0 1 0 7.07 7.07L13.3 18.77"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
    />
  </svg>
);

const PencilIcon = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden>
    <path
      d="M4 20h4.5L19 9.5 14.5 5 4 15.5V20z"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinejoin="round"
    />
    <path d="M12.5 7l4.5 4.5" stroke="currentColor" strokeWidth="1.6" />
  </svg>
);

const EditWorkTypeDrawer = ({
  open,
  projectId,
  section,
  workType,
  siblingWorkTypes = [],
  planFixed = false,
  onClose,
  onSaved,
  onRequestDelete,
}) => {
  const [form, setForm] = useState(emptyForm);
  const [readiness, setReadiness] = useState(0);
  const [predecessorIds, setPredecessorIds] = useState([]);
  const [successorIds, setSuccessorIds] = useState([]);
  const [projectSections, setProjectSections] = useState([]);
  const [relationsLoading, setRelationsLoading] = useState(false);
  const [relationsError, setRelationsError] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open || !workType) return;
    setForm({
      name: workType.name || "",
      unit_weight_percent: Number(workType.unit_weight_percent) || 0,
      plan_start_date: toDateInputValue(workType.plan_start_date),
      plan_end_date: toDateInputValue(workType.plan_end_date),
      fact_start_date: toDateInputValue(workType.fact_start_date),
      fact_end_date: toDateInputValue(workType.fact_end_date),
      completion_percent: clampPercent(workType.completion_percent),
      order_index: workType.order_index ?? 0,
    });
    setReadiness(Number(workType.readiness_plan_today_percent) || 0);
    setPredecessorIds(resolveWorkTypeRelationIds(workType, "predecessor"));
    setSuccessorIds(resolveWorkTypeRelationIds(workType, "successor"));
    setError("");
    setSaving(false);
    setRelationsError("");
  }, [open, workType]);

  useEffect(() => {
    if (!open || !projectId) return undefined;

    let cancelled = false;
    (async () => {
      try {
        setRelationsLoading(true);
        setRelationsError("");
        const data = await getProjectWorkTypesBySection(projectId);
        if (cancelled) return;
        setProjectSections(Array.isArray(data?.sections) ? data.sections : []);
      } catch (e) {
        if (!cancelled) {
          setProjectSections([]);
          setRelationsError(
            e?.message || "Не удалось загрузить виды работ проекта",
          );
        }
      } finally {
        if (!cancelled) setRelationsLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [open, projectId]);

  useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = (e) => {
      if (e.key === "Escape" && !saving) onClose();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [open, saving, onClose]);

  const sectionLabel = useMemo(() => {
    const { code, title } = splitSectionName(section?.name);
    return title ? `${code} / ${title}` : code;
  }, [section]);

  const isMilestone = Boolean(workType?.is_milestone);

  const weightSum = useMemo(() => {
    const others = siblingWorkTypes
      .filter((wt) => wt.id !== workType?.id && !wt.is_milestone)
      .reduce((sum, wt) => sum + (Number(wt.unit_weight_percent) || 0), 0);
    return others + (Number(form.unit_weight_percent) || 0);
  }, [siblingWorkTypes, workType?.id, form.unit_weight_percent]);

  const weightOk = Math.abs(weightSum - 100) < 0.05;

  const contribution = useMemo(() => {
    const weight = Number(form.unit_weight_percent) || 0;
    const completion = clampPercent(form.completion_percent);
    return (weight * completion) / 100;
  }, [form.unit_weight_percent, form.completion_percent]);

  const workTypeLookup = useMemo(() => {
    const map = new Map();
    for (const sec of projectSections) {
      const { code } = splitSectionName(sec.name);
      for (const wt of sec.work_types || []) {
        map.set(wt.id, {
          name: wt.name,
          sectionCode: code,
          isMilestone: Boolean(wt.is_milestone),
        });
      }
    }
    return map;
  }, [projectSections]);

  const getWorkTypeLabel = useCallback(
    (id) => {
      const item = workTypeLookup.get(id);
      if (!item) return `ID ${id}`;
      const name = item.isMilestone ? `◆ ${item.name}` : item.name;
      return item.sectionCode ? `${item.sectionCode} / ${name}` : name;
    },
    [workTypeLookup],
  );

  const planDuration = calcDurationDays(form.plan_start_date, form.plan_end_date);
  const factDuration = calcDurationDays(form.fact_start_date, form.fact_end_date);

  if (!open || !workType) return null;

  const setField = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setError("");
  };

  const handleSave = async () => {
    if (!form.name.trim()) {
      setError(
        isMilestone ? "Укажите название вехи" : "Укажите название вида работ",
      );
      return;
    }

    try {
      setSaving(true);
      setError("");

      const planDate = form.plan_start_date || null;
      const factDate = form.fact_start_date || null;

      const payload = isMilestone
        ? {
            name: form.name.trim(),
            is_milestone: true,
            unit_weight_percent: 0,
            plan_start_date: planDate,
            plan_end_date: planDate,
            fact_start_date: factDate,
            fact_end_date: factDate,
            completion_percent: factDate ? 100 : 0,
            order_index: form.order_index ?? 0,
            predecessor_ids: predecessorIds,
            successor_ids: successorIds,
          }
        : {
            name: form.name.trim(),
            is_milestone: false,
            unit_weight_percent: Number(form.unit_weight_percent) || 0,
            plan_start_date: form.plan_start_date || null,
            plan_end_date: form.plan_end_date || null,
            fact_start_date: form.fact_start_date || null,
            fact_end_date: form.fact_end_date || null,
            completion_percent: clampPercent(form.completion_percent),
            order_index: form.order_index ?? 0,
            predecessor_ids: predecessorIds,
            successor_ids: successorIds,
          };

      const updated = await updateProjectProgressWorkType(
        projectId,
        workType.id,
        payload,
      );
      onSaved?.(updated);
      onClose();
    } catch (e) {
      setError(
        e?.message ||
          (isMilestone
            ? "Не удалось сохранить веху"
            : "Не удалось сохранить вид работ"),
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <div
        className="edit-wt-drawer-backdrop"
        onClick={() => !saving && onClose()}
      />
      <aside className="edit-wt-drawer" role="dialog" aria-modal="true">
        <div className="edit-wt-drawer__header">
          <div className="edit-wt-drawer__header-main">
            <span className="edit-wt-drawer__header-icon" aria-hidden>
              <PencilIcon size={18} />
            </span>
            <div className="edit-wt-drawer__titles">
              <input
                className="edit-wt-drawer__name-input"
                value={form.name}
                onChange={(e) => setField("name", e.target.value)}
                disabled={saving}
              />
              <div className="edit-wt-drawer__section">{sectionLabel}</div>
            </div>
          </div>
          <button
            type="button"
            className="edit-wt-drawer__close"
            onClick={onClose}
            disabled={saving}
            aria-label="Закрыть"
          >
            ×
          </button>
        </div>

        <div className="edit-wt-drawer__body">
          {planFixed && (
            <div className="edit-wt-drawer__fixed-banner">
              <span aria-hidden>🔒</span>
              {isMilestone
                ? "План раздела зафиксирован. Плановые сроки недоступны для изменения."
                : "План раздела зафиксирован. Плановые сроки и веса недоступны для изменения."}
            </div>
          )}

          {isMilestone ? (
            <>
              <section className="edit-wt-drawer__block">
                <div className="edit-wt-drawer__term-title">
                  <span className="edit-wt-drawer__term-dot edit-wt-drawer__term-dot--plan" />
                  Плановая дата
                </div>
                {planFixed ? (
                  <div className="edit-wt-drawer__readonly-value">
                    {formatDate(form.plan_start_date)}
                  </div>
                ) : (
                  <input
                    type="date"
                    className="edit-wt-drawer__input"
                    value={form.plan_start_date}
                    onChange={(e) => {
                      const value = e.target.value;
                      setForm((prev) => ({
                        ...prev,
                        plan_start_date: value,
                        plan_end_date: value,
                      }));
                      setError("");
                    }}
                    disabled={saving}
                  />
                )}
              </section>

              <section className="edit-wt-drawer__block">
                <div className="edit-wt-drawer__term-title">
                  <span className="edit-wt-drawer__term-dot edit-wt-drawer__term-dot--fact" />
                  Фактическая дата
                </div>
                <input
                  type="date"
                  className="edit-wt-drawer__input"
                  value={form.fact_start_date}
                  onChange={(e) => {
                    const value = e.target.value;
                    setForm((prev) => ({
                      ...prev,
                      fact_start_date: value,
                      fact_end_date: value,
                    }));
                    setError("");
                  }}
                  disabled={saving}
                />
              </section>
            </>
          ) : (
            <>
          <section className="edit-wt-drawer__block">
            <h3 className="edit-wt-drawer__block-title">Удельный вес</h3>
            {!planFixed && (
              <p className="edit-wt-drawer__hint-label">Укажите удельный вес</p>
            )}
            {planFixed ? (
              <div className="edit-wt-drawer__readonly-value">
                {formatPercent(form.unit_weight_percent, 0)}
              </div>
            ) : (
              <div className="edit-wt-drawer__weight-controls">
                <div className="edit-wt-drawer__weight-input">
                  <input
                    type="number"
                    min="0"
                    max="100"
                    step="0.1"
                    value={form.unit_weight_percent}
                    onChange={(e) =>
                      setField("unit_weight_percent", e.target.value)
                    }
                    disabled={saving}
                  />
                  <span className="edit-wt-drawer__weight-affix">%</span>
                  <span className="edit-wt-drawer__weight-edit" aria-hidden>
                    <PencilIcon size={15} />
                  </span>
                </div>
                <input
                  type="range"
                  className="edit-wt-drawer__weight-range"
                  min="0"
                  max="100"
                  step="0.1"
                  value={clampPercent(form.unit_weight_percent)}
                  onChange={(e) =>
                    setField("unit_weight_percent", Number(e.target.value))
                  }
                  disabled={saving}
                  aria-label="Удельный вес"
                />
              </div>
            )}
            <p className="edit-wt-drawer__help">
              Доля влияет на расчёт % выполнения раздела. Сумма всех долей должна
              быть 100%.
            </p>
            {!planFixed && (
              <div
                className={`edit-wt-drawer__weight-banner${
                  weightOk
                    ? " edit-wt-drawer__weight-banner--ok"
                    : " edit-wt-drawer__weight-banner--warn"
                }`}
              >
                <span className="edit-wt-drawer__weight-dot" aria-hidden />
                {weightOk
                  ? `Сумма долей: ${formatPercent(weightSum, 0)} — всё корректно`
                  : `Сумма долей: ${formatPercent(weightSum, 1)} — проверьте веса раздела`}
              </div>
            )}
          </section>

          <section className="edit-wt-drawer__block">
            <div className="edit-wt-drawer__term-title">
              <span className="edit-wt-drawer__term-dot edit-wt-drawer__term-dot--plan" />
              Плановый срок
            </div>
            {planFixed ? (
              <div className="edit-wt-drawer__readonly-dates">
                <div>
                  Дата начала: <strong>{formatDate(form.plan_start_date)}</strong>
                </div>
                <div>
                  Дата окончания: <strong>{formatDate(form.plan_end_date)}</strong>
                </div>
                <div className="edit-wt-drawer__duration">
                  Длительность: {formatDaysWord(planDuration)}
                </div>
              </div>
            ) : (
              <>
                <div className="edit-wt-drawer__dates">
                  <div>
                    <label className="edit-wt-drawer__sublabel">Дата начала</label>
                    <input
                      type="date"
                      className="edit-wt-drawer__input"
                      value={form.plan_start_date}
                      onChange={(e) =>
                        setField("plan_start_date", e.target.value)
                      }
                      disabled={saving}
                    />
                  </div>
                  <div>
                    <label className="edit-wt-drawer__sublabel">
                      Дата окончания
                    </label>
                    <input
                      type="date"
                      className="edit-wt-drawer__input"
                      value={form.plan_end_date}
                      onChange={(e) => setField("plan_end_date", e.target.value)}
                      disabled={saving}
                    />
                  </div>
                </div>
                <div className="edit-wt-drawer__duration">
                  Длительность: {formatDaysWord(planDuration)}
                </div>
              </>
            )}
          </section>

          <section className="edit-wt-drawer__block">
            <div className="edit-wt-drawer__term-title">
              <span className="edit-wt-drawer__term-dot edit-wt-drawer__term-dot--fact" />
              Фактический срок
            </div>
            <div className="edit-wt-drawer__dates">
              <div>
                <label className="edit-wt-drawer__sublabel">Дата начала</label>
                <input
                  type="date"
                  className="edit-wt-drawer__input"
                  value={form.fact_start_date}
                  onChange={(e) => setField("fact_start_date", e.target.value)}
                  disabled={saving}
                />
              </div>
              <div>
                <label className="edit-wt-drawer__sublabel">Дата окончания</label>
                <input
                  type="date"
                  className="edit-wt-drawer__input"
                  value={form.fact_end_date}
                  onChange={(e) => setField("fact_end_date", e.target.value)}
                  disabled={saving}
                />
              </div>
            </div>
            <div className="edit-wt-drawer__duration">
              Длительность: {formatDaysWord(factDuration)}
            </div>
          </section>

          <section className="edit-wt-drawer__block">
            <div className="edit-wt-drawer__readiness">
              <span>План готовности на сегодня</span>
              <strong>{formatPercent(readiness, 0)}</strong>
            </div>
          </section>

          <section className="edit-wt-drawer__block">
            <h3 className="edit-wt-drawer__block-title">% выполнения раздела</h3>
            <div className="edit-wt-drawer__completion-row">
              <div className="edit-wt-drawer__completion-input">
                <input
                  id="wt-completion"
                  type="number"
                  min="0"
                  max="100"
                  step="1"
                  value={form.completion_percent}
                  onChange={(e) =>
                    setField("completion_percent", clampPercent(e.target.value))
                  }
                  disabled={saving}
                />
                <span>%</span>
              </div>
              <input
                id="wt-completion-range"
                type="range"
                min="0"
                max="100"
                step="1"
                value={clampPercent(form.completion_percent)}
                onChange={(e) =>
                  setField("completion_percent", Number(e.target.value))
                }
                disabled={saving}
              />
            </div>
          </section>

          <section className="edit-wt-drawer__block">
            <h3 className="edit-wt-drawer__block-title">Вклад в раздел</h3>
            <div className="edit-wt-drawer__contribution">
              <div className="edit-wt-drawer__contribution-caption">
                Расчёт вклада в раздел
              </div>
              <div className="edit-wt-drawer__contribution-formula">
                <span>Удельный вес вида работ × % выполнения вида работ</span>
                <strong>
                  {formatPercent(form.unit_weight_percent, 0)} ×{" "}
                  {formatPercent(form.completion_percent, 0)}
                </strong>
              </div>
              <div className="edit-wt-drawer__contribution-result">
                <span>Вклад в раздел</span>
                <strong>{formatPercent(contribution, 1)}</strong>
              </div>
            </div>
          </section>
            </>
          )}

          <section className="edit-wt-drawer__block">
            <h3 className="edit-wt-drawer__block-title">
              Связь между видами работ и вехами
            </h3>

            {relationsError && (
              <p className="edit-wt-drawer__relations-error">{relationsError}</p>
            )}

            <WorkTypeRelationPicker
              label="Предшественник"
              icon={LinkIcon}
              selectedIds={predecessorIds}
              onSelectedIdsChange={setPredecessorIds}
              excludedIds={successorIds}
              currentWorkTypeId={workType.id}
              sections={projectSections}
              getWorkTypeLabel={getWorkTypeLabel}
              loading={relationsLoading}
              disabled={saving}
            />

            <WorkTypeRelationPicker
              label="Последующий"
              icon={LinkIcon}
              selectedIds={successorIds}
              onSelectedIdsChange={setSuccessorIds}
              excludedIds={predecessorIds}
              currentWorkTypeId={workType.id}
              sections={projectSections}
              getWorkTypeLabel={getWorkTypeLabel}
              loading={relationsLoading}
              disabled={saving}
            />
          </section>

          {error && <div className="edit-wt-drawer__error">{error}</div>}
        </div>

        <div className="edit-wt-drawer__footer">
          <div className="edit-wt-drawer__footer-actions">
            <button
              type="button"
              className="edit-wt-drawer__save"
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? "Сохранение..." : "Сохранить"}
            </button>
            <button
              type="button"
              className="edit-wt-drawer__cancel"
              onClick={onClose}
              disabled={saving}
            >
              Отмена
            </button>
          </div>
          {!planFixed && (
            <button
              type="button"
              className="edit-wt-drawer__delete"
              onClick={() => onRequestDelete?.(workType)}
              disabled={saving}
            >
              Удалить {isMilestone ? "веху" : "вид работ"}
            </button>
          )}
        </div>
      </aside>
    </>
  );
};

export default EditWorkTypeDrawer;
